import { useCallback, useEffect, useId, useRef, useState } from 'react';
import './NumberScrubField.css';

const SCRUB_PX_PER_STEP = 3;
const MIN_DRAG_PX = 4;

/** One selectable unit in the optional unit dropdown. */
export interface NumberScrubUnitOption {
  value: string;
  label: string;
}

export interface NumberScrubFieldProps {
  /** Label text shown above the value box. Also the vertical-scrub handle. */
  label: string;
  /** Current numeric value. */
  value: number;
  /** Called with the next (clamped, step-snapped) value. */
  onChange: (next: number) => void;
  /**
   * Called once when a *gesture* ends, and only if the value changed during it:
   * the label scrub released, a typed value committed (blur/Enter), or a
   * stepper pressed (instantaneous, so its own boundary).
   *
   * `onChange` fires continuously while scrubbing; this fires once, when the
   * user has finished expressing an intent. Consumers that record what the user
   * did — rather than react live — want this, so that a scrub and a run of
   * stepper presses don't produce different numbers of records for the same
   * action. See sim-core's `CommitCause.user_edit`.
   */
  onChangeEnd?: (value: number) => void;
  /** Optional `id` for the input. Auto-generated if omitted. */
  id?: string;
  /** Optional lower bound (inclusive). No lower clamp when omitted. */
  min?: number;
  /** Optional upper bound (inclusive). No upper clamp when omitted. */
  max?: number;
  /**
   * Increment the value snaps to (typed, scrubbed, or spun). Defaults to `1`
   * (integer behaviour). Use a fractional step (e.g. `0.1`) for decimals.
   */
  step?: number;
  /**
   * Optional unit suffix shown in a box to the right (e.g. "px"). Ignored when
   * `unitOptions` is provided.
   */
  unit?: string;
  /**
   * Optional unit choices. When provided, the unit box renders a `<select>`
   * dropdown instead of a static suffix. Pair with `unitValue` / `onUnitChange`.
   */
  unitOptions?: NumberScrubUnitOption[];
  /** Currently selected unit (the controlled value of the dropdown). */
  unitValue?: string;
  /** Called with the next unit when the dropdown selection changes. */
  onUnitChange?: (next: string) => void;
  /** When true, the value input, scrub handle, and unit dropdown are inert. */
  disabled?: boolean;
  /**
   * When true, the value box is **not typable** — but the steppers and the
   * drag-to-scrub label still change it. Use for a display-plus-steppers field
   * (the value is shown, adjusted only by ▲/▼ or scrub), distinct from
   * `disabled` (which makes everything inert).
   */
  readOnly?: boolean;
  /**
   * Show a connected vertical up/down stepper on the right of the box. Each
   * press nudges the value by `step` (clamped). The label remains a
   * drag-to-scrub handle for larger changes.
   */
  stepper?: boolean;
  /**
   * Hide the visible label (and with it the drag-to-scrub handle), e.g. when
   * an external label already names the control. `label` is still applied as
   * the input's `aria-label` for accessibility.
   */
  hideLabel?: boolean;
}

/** Decimal places implied by a step (e.g. 0.1 → 1, 1 → 0). */
function stepDecimals(step: number): number {
  if (!Number.isFinite(step)) return 0;
  const s = String(step);
  const dot = s.indexOf('.');
  return dot === -1 ? 0 : s.length - dot - 1;
}

/**
 * A numeric input whose label doubles as a vertical drag-to-scrub handle.
 * Drag up/down on the label to adjust the value, or type directly. Typed
 * edits are held as a draft and committed on blur / Enter. Supports an
 * optional unit suffix or, with `unitOptions`, an inline unit dropdown.
 */
export function NumberScrubField({
  label,
  value,
  onChange,
  onChangeEnd,
  id,
  min,
  max,
  step = 1,
  unit,
  unitOptions,
  unitValue,
  onUnitChange,
  disabled = false,
  readOnly = false,
  stepper = false,
  hideLabel = false,
}: NumberScrubFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const decimals = stepDecimals(step);

  const [draftValue, setDraftValue] = useState<string>(String(value));
  const isEditingRef = useRef(false);
  const scrubRef = useRef<{
    pointerId: number;
    startY: number;
    startValue: number;
    el: HTMLElement;
  } | null>(null);
  const scrubMovedRef = useRef(false);

  const clamp = useCallback(
    (n: number) => {
      let r = Math.round(n / step) * step;
      // Snap away floating-point dust introduced by the divide/multiply.
      r = Number(r.toFixed(decimals));
      if (min !== undefined) r = Math.max(min, r);
      if (max !== undefined) r = Math.min(max, r);
      return r;
    },
    [min, max, step, decimals],
  );

  const inRange = useCallback(
    (n: number) =>
      (min === undefined || n >= min) && (max === undefined || n <= max),
    [min, max],
  );

  useEffect(() => {
    if (!isEditingRef.current) {
      setDraftValue(String(value));
    }
  }, [value]);

  // Whether the value moved during the gesture in progress. Makes `endGesture`
  // idempotent and keeps a bare click or a tab-through from reporting an edit.
  const changedRef = useRef(false);
  const onChangeEndRef = useRef(onChangeEnd);
  onChangeEndRef.current = onChangeEnd;

  /**
   * Report the end of an intent — the scrub released, the typed value
   * committed, or a stepper pressed. Fires at most once per gesture.
   */
  const endGesture = useCallback((next: number) => {
    if (!changedRef.current) return;
    changedRef.current = false;
    onChangeEndRef.current?.(next);
  }, []);

  const commitDraft = useCallback(() => {
    const n = Number(draftValue);
    if (!Number.isFinite(n)) {
      setDraftValue(String(value));
      return;
    }
    const next = clamp(n);
    if (next !== value) {
      changedRef.current = true;
      onChange(next);
    }
    // Blur / Enter is the boundary for the typed path. `endGesture` no-ops
    // unless something actually changed — which the live `onChange` above (or
    // the one on the input itself) will have recorded.
    endGesture(next);
  }, [draftValue, value, onChange, clamp, endGesture]);

  const endLabelPointer = useCallback(
    (e: React.PointerEvent<HTMLLabelElement>) => {
      const s = scrubRef.current;
      if (!s || e.pointerId !== s.pointerId) return;
      try {
        s.el.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      scrubRef.current = null;
      endGesture(value); // spring release
    },
    [endGesture, value],
  );

  const handleLabelPointerDown = useCallback(
    (e: React.PointerEvent<HTMLLabelElement>) => {
      if (disabled) return;
      scrubMovedRef.current = false;
      const el = e.currentTarget;
      el.setPointerCapture(e.pointerId);
      scrubRef.current = {
        pointerId: e.pointerId,
        startY: e.clientY,
        startValue: value,
        el,
      };
    },
    [disabled, value],
  );

  const handleLabelPointerMove = useCallback(
    (e: React.PointerEvent<HTMLLabelElement>) => {
      const s = scrubRef.current;
      if (!s || e.pointerId !== s.pointerId) return;
      const dy = e.clientY - s.startY;
      if (Math.abs(dy) < MIN_DRAG_PX) return;
      scrubMovedRef.current = true;
      e.preventDefault();
      const steps = Math.round(-dy / SCRUB_PX_PER_STEP);
      const next = clamp(s.startValue + steps * step);
      if (next !== value) {
        changedRef.current = true;
        onChange(next);
        setDraftValue(String(next));
      }
    },
    [clamp, onChange, step, value],
  );

  const handleLabelClick = useCallback(
    (e: React.MouseEvent<HTMLLabelElement>) => {
      if (scrubMovedRef.current) {
        e.preventDefault();
        scrubMovedRef.current = false;
      }
    },
    [],
  );

  const stepBy = useCallback(
    (dir: 1 | -1) => {
      if (disabled) return;
      const next = clamp(value + dir * step);
      if (next !== value) {
        changedRef.current = true;
        onChange(next);
        setDraftValue(String(next));
        // A stepper press is instantaneous — the press IS the whole gesture,
        // so it's its own boundary rather than waiting for a release.
        endGesture(next);
      }
    },
    [disabled, clamp, value, step, onChange, endGesture],
  );

  const atMax = max !== undefined && value >= max;
  const atMin = min !== undefined && value <= min;

  return (
    <div className="alc-scrub-field">
      {!hideLabel && (
        <label
          htmlFor={inputId}
          className="alc-scrub-label"
          title={`Drag vertically on “${label}” to adjust, or tap to focus the value`}
          onPointerDown={handleLabelPointerDown}
          onPointerMove={handleLabelPointerMove}
          onPointerUp={endLabelPointer}
          onPointerCancel={endLabelPointer}
          onClick={handleLabelClick}
        >
          {label}
        </label>
      )}
      <div
        className={[
          'alc-scrub-control',
          unitOptions && 'alc-scrub-control--with-select',
          stepper && 'alc-scrub-control--stepper',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <input
          id={inputId}
          type="number"
          {...(hideLabel ? { 'aria-label': label } : {})}
          {...(min !== undefined ? { min } : {})}
          {...(max !== undefined ? { max } : {})}
          step={step}
          disabled={disabled}
          readOnly={readOnly}
          value={draftValue}
          onFocus={() => {
            isEditingRef.current = true;
          }}
          onBlur={() => {
            isEditingRef.current = false;
            commitDraft();
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            (e.currentTarget as HTMLInputElement).blur();
          }}
          onChange={(e) => {
            const raw = e.target.value;
            setDraftValue(raw);
            const n = Number(raw);
            // Keep live updates for in-range edits, but allow temporary
            // out-of-range drafts (e.g. typing "0" before a larger number)
            // until blur / Enter commit.
            if (Number.isFinite(n) && inRange(n)) {
              // Typing commits LIVE, so by blur `value` already equals the
              // typed number — mark the edit here or `commitDraft` would find
              // nothing changed and never report the boundary.
              changedRef.current = true;
              onChange(clamp(n));
            }
          }}
          className="alc-scrub-input"
        />
        {unitOptions ? (
          <div className="alc-scrub-unit alc-scrub-unit--select">
            <select
              className="alc-scrub-unit-select"
              value={unitValue}
              disabled={disabled}
              aria-label={`${label} unit`}
              onChange={(e) => onUnitChange?.(e.target.value)}
            >
              {unitOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        ) : unit !== undefined ? (
          <div className="alc-scrub-unit" aria-hidden="true">
            <span className="alc-scrub-unit-text">{unit}</span>
          </div>
        ) : null}
        {stepper && (
          <div className="alc-scrub-stepper">
            <button
              type="button"
              tabIndex={-1}
              className="alc-scrub-step alc-scrub-step--up"
              disabled={disabled || atMax}
              aria-label={`Increase ${label}`}
              onClick={() => stepBy(1)}
            >
              <svg width="12" height="8" viewBox="0 0 12 8" aria-hidden="true">
                <path
                  d="M1 6l5-4 5 4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              tabIndex={-1}
              className="alc-scrub-step alc-scrub-step--down"
              disabled={disabled || atMin}
              aria-label={`Decrease ${label}`}
              onClick={() => stepBy(-1)}
            >
              <svg width="12" height="8" viewBox="0 0 12 8" aria-hidden="true">
                <path
                  d="M1 2l5 4 5-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
